/**
 * PAREERO HEALTH & FITNESS PLANNER - Standalone Application Logic
 * No external dependencies. Vanilla JS (ES2020+).
 * Runs completely locally in browser and integrates seamlessly with Shopify.
 */

(function () {
  "use strict";

  // Global state object
  const state = {
    profile: {
      age: 28,
      sex: "female",
      height: 165, // cm
      heightFt: 5,
      heightIn: 5,
      weight: 68, // kg
      goalWeight: 62, // kg
      activity: "light",
      fitnessLevel: "beginner",
      goal: "lose_weight",
      units: "metric", // "metric" or "imperial"
      dietPreference: "balanced",
      mealsCount: 3,
      equipment: "dumbbells",
      location: "home",
      themePalette: "heritage-gold",
      themeFont: "modern-sans",
      themeMood: "mood-cream"
    },
    diary: {}, // key: "YYYY-MM-DD", val: array of foods
    water: {}, // key: "YYYY-MM-DD", val: ml
    steps: {}, // key: "YYYY-MM-DD", val: count
    sleep: {}, // key: "YYYY-MM-DD", val: hours
    habits: {}, // key: "YYYY-MM-DD", val: array of completed habit ids
    weightHistory: [], // array of { date, weight, bmi, note }
    measurements: [], // array of { date, chest, waist, hips, arm, thigh }
    overloadLogs: [], // array of { id, exerciseId, date, weight, reps, sets, rpe, note }
    activeMealPlan: null,
    groceryListChecked: {},
    currentDate: getTodayDateString(),
    currentView: "dashboard",
    currentSeoArticle: null
  };

  // Helper date function (YYYY-MM-DD in local time)
  function getTodayDateString() {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  // LocalStorage Keys
  const STORAGE_KEY = "pareero_health_app_data_v1";

  // Load state from localStorage
  function loadPersistedState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.profile) state.profile = Object.assign({}, state.profile, parsed.profile);
        if (parsed.diary) state.diary = parsed.diary;
        if (parsed.water) state.water = parsed.water;
        if (parsed.steps) state.steps = parsed.steps;
        if (parsed.sleep) state.sleep = parsed.sleep;
        if (parsed.habits) state.habits = parsed.habits;
        if (parsed.weightHistory) state.weightHistory = parsed.weightHistory;
        if (parsed.measurements) state.measurements = parsed.measurements;
        if (parsed.overloadLogs) state.overloadLogs = parsed.overloadLogs;
        if (parsed.activeMealPlan) state.activeMealPlan = parsed.activeMealPlan;
        if (parsed.groceryListChecked) state.groceryListChecked = parsed.groceryListChecked;
      } else {
        // Seed default initial weight history for immediate visual charts
        seedInitialData();
      }
    } catch (e) {
      console.warn("Could not parse Pareero stored data:", e);
      seedInitialData();
    }
  }

  // Seed friendly sample history if brand new user
  function seedInitialData() {
    const today = new Date();
    const sampleDates = [];
    for (let i = 14; i >= 0; i -= 2) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const str = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      sampleDates.push(str);
    }
    state.weightHistory = [
      { date: sampleDates[0], weight: 70.2, bmi: 25.8 },
      { date: sampleDates[1], weight: 69.8, bmi: 25.6 },
      { date: sampleDates[2], weight: 69.4, bmi: 25.5 },
      { date: sampleDates[3], weight: 69.0, bmi: 25.3 },
      { date: sampleDates[4], weight: 68.6, bmi: 25.2 },
      { date: sampleDates[5], weight: 68.3, bmi: 25.1 },
      { date: sampleDates[6], weight: 68.0, bmi: 25.0 }
    ];
    state.water[state.currentDate] = 1750;
    state.steps[state.currentDate] = 6400;
    state.sleep[state.currentDate] = 7.5;
    state.habits[state.currentDate] = ["h1", "h4", "h6"];
  }

  // Save state to localStorage
  function saveState() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error("Pareero could not save state to localStorage:", e);
    }
  }

  // Unit conversion helpers
  function kgToLbs(kg) {
    return Math.round(kg * 2.20462 * 10) / 10;
  }
  function lbsToKg(lbs) {
    return Math.round((lbs / 2.20462) * 10) / 10;
  }
  function cmToFtIn(cm) {
    const totalInches = cm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return { feet, inches };
  }
  function ftInToCm(feet, inches) {
    return Math.round((feet * 12 + inches) * 2.54);
  }

  // Calculations Engine
  function calculateMetrics() {
    const p = state.profile;
    const weightKg = p.units === "imperial" ? lbsToKg(p.weight) : Number(p.weight);
    const heightCm = p.units === "imperial" ? ftInToCm(p.heightFt, p.heightIn) : Number(p.height);
    const heightM = heightCm / 100;

    // 1. BMI
    const bmi = Math.round((weightKg / (heightM * heightM)) * 10) / 10;
    let bmiCategory = "Normal / Healthy Weight";
    let bmiBadgeClass = "badge-success";
    let bmiAdvice = "Your weight is within the standard healthy screening range for your height.";

    if (bmi < 18.5) {
      bmiCategory = "Underweight";
      bmiBadgeClass = "badge-warning";
      bmiAdvice = "A BMI below 18.5 may indicate lower bone density or muscle mass. Consider nutrient-rich calorie sources and strength training.";
    } else if (bmi >= 25 && bmi < 29.9) {
      bmiCategory = "Overweight";
      bmiBadgeClass = "badge-warning";
      bmiAdvice = "A BMI between 25 and 29.9 is a prompt to evaluate body composition, waist circumference, and daily physical activity.";
    } else if (bmi >= 30 && bmi < 34.9) {
      bmiCategory = "Obesity (Class I)";
      bmiBadgeClass = "badge-danger";
      bmiAdvice = "A moderate calorie deficit and consistent low-impact daily movement can substantially support cardiovascular and metabolic health.";
    } else if (bmi >= 35) {
      bmiCategory = "Obesity (Class II/III)";
      bmiBadgeClass = "badge-danger";
      bmiAdvice = "Consult a physician or registered dietitian for personalized clinical support and safe monitoring.";
    }

    // 2. Healthy Weight Range (for BMI 18.5 to 24.9)
    const minHealthyKg = Math.round(18.5 * heightM * heightM * 10) / 10;
    const maxHealthyKg = Math.round(24.9 * heightM * heightM * 10) / 10;

    // 3. BMR (Mifflin-St Jeor Equation)
    let bmr = 0;
    if (p.sex === "male") {
      bmr = 10 * weightKg + 6.25 * heightCm - 5 * p.age + 5;
    } else {
      bmr = 10 * weightKg + 6.25 * heightCm - 5 * p.age - 161;
    }
    bmr = Math.round(bmr);

    // 4. Activity Multipliers
    const multipliers = {
      sedentary: 1.2,
      light: 1.375,
      moderate: 1.55,
      very: 1.725,
      extra: 1.9
    };
    const actMult = multipliers[p.activity] || 1.375;
    const tdee = Math.round(bmr * actMult);

    // 5. Goal Calorie Calculation & Clinical Safety Checks
    let goalCalories = tdee;
    let deficitOrSurplus = 0;
    let safetyAlert = null;

    if (p.goal === "lose_weight") {
      deficitOrSurplus = -450;
      goalCalories = tdee - 450;
      const minSafe = p.sex === "female" ? 1200 : 1500;
      if (goalCalories < minSafe) {
        safetyAlert = `Safety Notice: A calorie target below ${minSafe} kcal/day is not recommended without clinical supervision. We have adjusted your suggested target to the minimum safe threshold of ${minSafe} kcal.`;
        goalCalories = minSafe;
      }
    } else if (p.goal === "gain_weight" || p.goal === "build_muscle") {
      deficitOrSurplus = 350;
      goalCalories = tdee + 350;
    }

    // 6. Macronutrient Distribution
    // Protein target: 1.6 - 2.0g per kg depending on goal
    let proteinMultiplier = 1.6;
    if (p.goal === "lose_weight") proteinMultiplier = 1.9; // preserve lean mass
    if (p.goal === "build_muscle") proteinMultiplier = 2.0;

    const targetProteinGrams = Math.round(weightKg * proteinMultiplier);
    const proteinCalories = targetProteinGrams * 4;

    // Fat: ~25% of goal calories
    const fatCalories = Math.round(goalCalories * 0.25);
    const targetFatGrams = Math.round(fatCalories / 9);

    // Carbs: Remaining calories
    const carbCalories = Math.max(0, goalCalories - proteinCalories - fatCalories);
    const targetCarbGrams = Math.round(carbCalories / 4);

    // 7. Water Intake (32-35 ml per kg + workout bonus)
    const targetWaterMl = Math.round(weightKg * 34);

    // 8. Progress Timeline
    const goalWeightKg = p.units === "imperial" ? lbsToKg(p.goalWeight) : Number(p.goalWeight);
    const weightDiff = Math.round(Math.abs(weightKg - goalWeightKg) * 10) / 10;
    let estimatedWeeks = 0;
    if (p.goal === "lose_weight") {
      // 0.45 kg per week
      estimatedWeeks = weightDiff > 0 ? Math.ceil(weightDiff / 0.45) : 0;
    } else if (p.goal === "gain_weight" || p.goal === "build_muscle") {
      // 0.3 kg per week
      estimatedWeeks = weightDiff > 0 ? Math.ceil(weightDiff / 0.3) : 0;
    }

    return {
      weightKg,
      heightCm,
      heightM,
      bmi,
      bmiCategory,
      bmiBadgeClass,
      bmiAdvice,
      minHealthyKg,
      maxHealthyKg,
      bmr,
      tdee,
      goalCalories,
      deficitOrSurplus,
      safetyAlert,
      targetProteinGrams,
      targetCarbGrams,
      targetFatGrams,
      targetWaterMl,
      goalWeightKg,
      weightDiff,
      estimatedWeeks
    };
  }

  // DOM Elements cache
  const root = document.getElementById("pareero-health-app");
  if (!root) {
    console.error("Root element #pareero-health-app not found.");
    return;
  }

  // Apply Theme & Font to Root
  function applyThemeSettings() {
    const p = state.profile;
    // Remove existing theme classes
    root.className = root.className
      .replace(/\bpareero-theme-\S+/g, "")
      .replace(/\bpareero-mood-\S+/g, "")
      .replace(/\bpareero-font-\S+/g, "")
      .trim();

    root.classList.add(`pareero-theme-${p.themePalette}`);
    root.classList.add(`pareero-${p.themeMood}`);
    root.classList.add(`pareero-font-${p.themeFont}`);
  }

  // Switch View Tab
  function switchView(viewName) {
    state.currentView = viewName;
    const views = root.querySelectorAll(".pareero-health-view");
    views.forEach((v) => {
      if (v.getAttribute("data-view") === viewName) {
        v.classList.add("active");
      } else {
        v.classList.remove("active");
      }
    });

    // Update active nav link
    const links = root.querySelectorAll(".pareero-health-nav-link");
    links.forEach((l) => {
      if (l.getAttribute("data-nav") === viewName) {
        l.classList.add("active");
      } else {
        l.classList.remove("active");
      }
    });

    // Re-render specific view content
    renderCurrentView(viewName);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // Render view-specific dynamic sections
  function renderCurrentView(viewName) {
    switch (viewName) {
      case "dashboard":
        renderDashboard();
        break;
      case "weight":
        renderWeightView();
        break;
      case "nutrition":
        renderNutritionView();
        break;
      case "diet-plans":
        renderDietPlansView();
        break;
      case "fitness":
        renderFitnessView();
        break;
      case "wellness":
        renderWellnessView();
        break;
      case "progress":
        renderProgressView();
        break;
      case "knowledge":
        renderKnowledgeView();
        break;
      default:
        break;
    }
  }

  // 1. DASHBOARD VIEW
  function renderDashboard() {
    const m = calculateMetrics();
    const p = state.profile;

    // Stats Bar
    const statsContainer = root.querySelector("#pareero-dash-stats");
    if (statsContainer) {
      const weightDisplay = p.units === "imperial" ? `${kgToLbs(m.weightKg)} lbs` : `${m.weightKg} kg`;
      const goalDisplay = p.units === "imperial" ? `${kgToLbs(m.goalWeightKg)} lbs` : `${m.goalWeightKg} kg`;
      statsContainer.innerHTML = `
        <div class="pareero-health-stat-pill">
          <div class="pareero-health-stat-icon">⚖️</div>
          <div>
            <div class="pareero-health-stat-val">${weightDisplay}</div>
            <div class="pareero-health-stat-lbl">Current Weight</div>
          </div>
        </div>
        <div class="pareero-health-stat-pill">
          <div class="pareero-health-stat-icon">🎯</div>
          <div>
            <div class="pareero-health-stat-val">${goalDisplay}</div>
            <div class="pareero-health-stat-lbl">Goal Weight</div>
          </div>
        </div>
        <div class="pareero-health-stat-pill">
          <div class="pareero-health-stat-icon">🔥</div>
          <div>
            <div class="pareero-health-stat-val">${m.goalCalories} kcal</div>
            <div class="pareero-health-stat-lbl">Target Calories</div>
          </div>
        </div>
        <div class="pareero-health-stat-pill">
          <div class="pareero-health-stat-icon">📊</div>
          <div>
            <div class="pareero-health-stat-val">BMI ${m.bmi}</div>
            <div class="pareero-health-stat-lbl">${m.bmiCategory}</div>
          </div>
        </div>
      `;
    }

    // Today's Nutrition Consumed vs Target
    const todayLog = state.diary[state.currentDate] || [];
    let consumedCalories = 0;
    let consumedProtein = 0;
    let consumedCarbs = 0;
    let consumedFat = 0;

    todayLog.forEach((item) => {
      const factor = item.portions || 1;
      consumedCalories += item.calories * factor;
      consumedProtein += item.protein * factor;
      consumedCarbs += item.carbs * factor;
      consumedFat += item.fat * factor;
    });

    consumedCalories = Math.round(consumedCalories);
    consumedProtein = Math.round(consumedProtein);
    consumedCarbs = Math.round(consumedCarbs);
    consumedFat = Math.round(consumedFat);

    const remainingCal = Math.max(0, m.goalCalories - consumedCalories);
    const calPercent = Math.min(100, Math.round((consumedCalories / m.goalCalories) * 100));

    const todayOverview = root.querySelector("#pareero-dash-today-nutrition");
    if (todayOverview) {
      todayOverview.innerHTML = `
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
          <div>
            <span style="font-size:12px; color:var(--pareero-text-muted); text-transform:uppercase; font-weight:700;">Calories Remaining</span>
            <div style="font-size:28px; font-weight:800; color:var(--pareero-dark);">${remainingCal} <span style="font-size:14px; font-weight:600; color:var(--pareero-text-muted);">kcal</span></div>
          </div>
          <div style="text-align:right;">
            <span style="font-size:12px; color:var(--pareero-text-muted);">Eaten: <strong>${consumedCalories}</strong> / Target: <strong>${m.goalCalories}</strong></span>
            <div style="font-size:13px; font-weight:700; color:var(--pareero-primary);">${calPercent}% complete</div>
          </div>
        </div>
        <div class="pareero-health-macro-bar">
          <div class="pareero-health-macro-segment pareero-health-segment-protein" style="width:${calPercent}%;"></div>
        </div>
        <div style="display:grid; grid-template-columns:repeat(3, 1fr); gap:10px; margin-top:16px; text-align:center;">
          <div style="background:var(--pareero-bg); padding:10px; border-radius:8px;">
            <div style="font-size:11px; font-weight:700; color:#3B82F6;">PROTEIN</div>
            <div style="font-size:16px; font-weight:800;">${consumedProtein}g <span style="font-size:11px; color:var(--pareero-text-muted);">/ ${m.targetProteinGrams}g</span></div>
          </div>
          <div style="background:var(--pareero-bg); padding:10px; border-radius:8px;">
            <div style="font-size:11px; font-weight:700; color:#EAB308;">CARBS</div>
            <div style="font-size:16px; font-weight:800;">${consumedCarbs}g <span style="font-size:11px; color:var(--pareero-text-muted);">/ ${m.targetCarbGrams}g</span></div>
          </div>
          <div style="background:var(--pareero-bg); padding:10px; border-radius:8px;">
            <div style="font-size:11px; font-weight:700; color:#EF4444;">FAT</div>
            <div style="font-size:16px; font-weight:800;">${consumedFat}g <span style="font-size:11px; color:var(--pareero-text-muted);">/ ${m.targetFatGrams}g</span></div>
          </div>
        </div>
      `;
    }

    // Dashboard Habits preview
    const dashHabits = root.querySelector("#pareero-dash-habits");
    if (dashHabits) {
      const activeIds = state.habits[state.currentDate] || [];
      dashHabits.innerHTML = PAREERO_DATA.habits.slice(0, 4).map((h) => {
        const isDone = activeIds.includes(h.id);
        return `
          <div class="pareero-health-habit-row ${isDone ? "completed" : ""}" data-habit-id="${h.id}">
            <div class="pareero-health-habit-info">
              <div class="pareero-health-habit-checkbox">${isDone ? "✓" : ""}</div>
              <div class="pareero-health-habit-text">
                <h5>${h.title}</h5>
                <p>${h.desc}</p>
              </div>
            </div>
          </div>
        `;
      }).join("");
    }
  }

  // 2. WEIGHT VIEW & CALCULATORS
  function renderWeightView() {
    const m = calculateMetrics();
    const p = state.profile;

    const calcCard = root.querySelector("#pareero-weight-calculator-card");
    if (calcCard) {
      const minWeightStr = p.units === "imperial" ? `${kgToLbs(m.minHealthyKg)} lbs` : `${m.minHealthyKg} kg`;
      const maxWeightStr = p.units === "imperial" ? `${kgToLbs(m.maxHealthyKg)} lbs` : `${m.maxHealthyKg} kg`;

      calcCard.innerHTML = `
        ${m.safetyAlert ? `
          <div class="pareero-health-safety-alert danger">
            <div class="pareero-health-safety-icon">⚠️</div>
            <div class="pareero-health-safety-text">
              <h4>Safety Advisory</h4>
              <p>${m.safetyAlert}</p>
            </div>
          </div>
        ` : ""}
        <div class="pareero-health-grid-2">
          <!-- BMI Box -->
          <div class="pareero-health-calc-result">
            <span class="pareero-health-result-label">Body Mass Index (BMI)</span>
            <div class="pareero-health-result-number">${m.bmi}</div>
            <span class="pareero-health-card-badge">${m.bmiCategory}</span>
            <p class="pareero-health-result-desc">${m.bmiAdvice}</p>
            <div style="font-size:11.5px; color:var(--pareero-text-muted); margin-top:8px;">
              *BMI is a general screening measure and does not diagnose medical conditions.
            </div>
          </div>

          <!-- Calories & Range Box -->
          <div class="pareero-health-calc-result" style="background:var(--pareero-card-bg);">
            <span class="pareero-health-result-label">Estimated Maintenance (TDEE)</span>
            <div class="pareero-health-result-number" style="color:var(--pareero-primary);">${m.tdee} <span style="font-size:16px;">kcal</span></div>
            <div style="margin:12px 0; font-size:13.5px; color:var(--pareero-dark-muted);">
              Estimated Basal Metabolic Rate (BMR): <strong>${m.bmr} kcal</strong><br>
              Recommended Goal Intake: <strong>${m.goalCalories} kcal / day</strong>
            </div>
            <div style="background:var(--pareero-bg); padding:10px; border-radius:8px; font-size:12.5px;">
              Healthy Weight Range for your height:<br>
              <strong>${minWeightStr} – ${maxWeightStr}</strong>
            </div>
          </div>
        </div>

        <!-- Progress Timeline & Goal Details -->
        <div class="pareero-health-card" style="margin-top:20px; background:var(--pareero-primary-light); border-color:var(--pareero-primary-border);">
          <div style="display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px;">
            <div>
              <h4 style="margin:0 0 4px; font-size:15px; color:var(--pareero-dark);">Goal Projection & Weekly Cadence</h4>
              <p style="margin:0; font-size:13px; color:var(--pareero-dark-muted);">
                ${m.weightDiff > 0 ? `Target difference: <strong>${m.weightDiff} ${p.units === "imperial" ? "lbs" : "kg"}</strong>.` : "You are currently at your target weight!"}
                Suggested weekly change: <strong>${p.goal === "lose_weight" ? "0.35–0.5 kg" : p.goal === "gain_weight" ? "0.25–0.4 kg" : "Maintenance"}</strong> per week.
              </p>
            </div>
            ${m.estimatedWeeks > 0 ? `
              <div style="background:var(--pareero-card-bg); padding:8px 16px; border-radius:10px; text-align:center; border:1px solid var(--pareero-card-border);">
                <div style="font-size:20px; font-weight:800; color:var(--pareero-primary);">~${m.estimatedWeeks} Weeks</div>
                <div style="font-size:11px; text-transform:uppercase; color:var(--pareero-text-muted);">Estimated Horizon</div>
              </div>
            ` : ""}
          </div>
        </div>

        <!-- How this is calculated Accordion -->
        <div class="pareero-health-accordion">
          <div class="pareero-health-accordion-header">
            <span>📐 How these calculations are derived (Mifflin-St Jeor Formula)</span>
            <span class="pareero-health-accordion-icon">▼</span>
          </div>
          <div class="pareero-health-accordion-content">
            <p><strong>1. Basal Metabolic Rate (BMR):</strong> Calculated using the clinical Mifflin-St Jeor equation:</p>
            <ul>
              <li><strong>Males:</strong> BMR = (10 × weight in kg) + (6.25 × height in cm) - (5 × age) + 5</li>
              <li><strong>Females:</strong> BMR = (10 × weight in kg) + (6.25 × height in cm) - (5 × age) - 161</li>
            </ul>
            <p><strong>2. Total Daily Energy Expenditure (TDEE):</strong> BMR is multiplied by your activity factor (1.2 for sedentary, 1.375 for lightly active, 1.55 for moderate, 1.725 for very active).</p>
            <p><strong>3. Goal Target:</strong> For weight loss, a moderate deficit of 400–450 kcal/day is applied (never below 1,200 kcal for women or 1,500 kcal for men). For healthy weight gain, a clean surplus of 350 kcal/day is applied to support muscle development.</p>
          </div>
        </div>
      `;
    }
  }

  // 3. NUTRITION & FOOD TRACKER VIEW
  function renderNutritionView() {
    const m = calculateMetrics();
    const searchInput = root.querySelector("#pareero-food-search-input");
    const categoryFilter = root.querySelector("#pareero-food-cat-filter");
    const foodListContainer = root.querySelector("#pareero-food-search-results");
    const diaryListContainer = root.querySelector("#pareero-diary-items-list");

    // Populate category filter options if empty
    if (categoryFilter && categoryFilter.options.length <= 1) {
      const categories = Array.from(new Set(PAREERO_DATA.foods.map((f) => f.category)));
      categories.forEach((c) => {
        const opt = document.createElement("option");
        opt.value = c;
        opt.textContent = c;
        categoryFilter.appendChild(opt);
      });
    }

    // Function to render food search results
    function filterFoods() {
      const query = (searchInput ? searchInput.value : "").trim().toLowerCase();
      const selectedCat = categoryFilter ? categoryFilter.value : "";

      const filtered = PAREERO_DATA.foods.filter((f) => {
        const matchesQuery =
          !query ||
          f.name.toLowerCase().includes(query) ||
          (f.urdu && f.urdu.includes(query)) ||
          f.category.toLowerCase().includes(query);
        const matchesCat = !selectedCat || f.category === selectedCat;
        return matchesQuery && matchesCat;
      });

      if (foodListContainer) {
        if (filtered.length === 0) {
          foodListContainer.innerHTML = `<div style="text-align:center; padding:20px; color:var(--pareero-text-muted);">No matching Pakistani foods found. Try adjusting your search.</div>`;
          return;
        }

        foodListContainer.innerHTML = filtered.slice(0, 20).map((f) => {
          return `
            <div class="pareero-health-food-item">
              <div class="pareero-health-food-info">
                <h4>${f.name} ${f.urdu ? `<span style="font-weight:400; color:var(--pareero-text-muted); font-size:12px;">(${f.urdu})</span>` : ""}</h4>
                <div class="pareero-health-food-meta">
                  <span class="pareero-health-food-badge">${f.category}</span>
                  Serving: <strong>${f.serving}</strong>
                </div>
              </div>
              <div class="pareero-health-food-macros">
                <div>
                  <div style="font-size:14px; font-weight:800; color:var(--pareero-primary);">${f.calories} kcal</div>
                  <div style="font-size:11px; color:var(--pareero-text-muted);">P: ${f.protein}g | C: ${f.carbs}g | F: ${f.fat}g</div>
                </div>
                <button class="pareero-health-btn pareero-health-btn-secondary pareero-health-btn-sm pareero-add-food-btn" data-food-id="${f.id}" title="Add to Today's Diary">
                  + Log
                </button>
              </div>
            </div>
          `;
        }).join("");
      }
    }

    filterFoods();

    // Render Today's Diary Log
    const todayLog = state.diary[state.currentDate] || [];
    let totCal = 0, totP = 0, totC = 0, totF = 0;
    todayLog.forEach((item) => {
      const p = item.portions || 1;
      totCal += item.calories * p;
      totP += item.protein * p;
      totC += item.carbs * p;
      totF += item.fat * p;
    });

    // Update Nutrition Header Targets
    const nutHeader = root.querySelector("#pareero-nutrition-header-stats");
    if (nutHeader) {
      nutHeader.innerHTML = `
        <div class="pareero-health-stat-pill">
          <div class="pareero-health-stat-icon">🔥</div>
          <div>
            <div class="pareero-health-stat-val">${Math.round(totCal)} / ${m.goalCalories} kcal</div>
            <div class="pareero-health-stat-lbl">Daily Energy Target</div>
          </div>
        </div>
        <div class="pareero-health-stat-pill">
          <div class="pareero-health-stat-icon">🥩</div>
          <div>
            <div class="pareero-health-stat-val">${Math.round(totP)} / ${m.targetProteinGrams}g</div>
            <div class="pareero-health-stat-lbl">Protein Goal</div>
          </div>
        </div>
        <div class="pareero-health-stat-pill">
          <div class="pareero-health-stat-icon">🍞</div>
          <div>
            <div class="pareero-health-stat-val">${Math.round(totC)} / ${m.targetCarbGrams}g</div>
            <div class="pareero-health-stat-lbl">Carbs Goal</div>
          </div>
        </div>
        <div class="pareero-health-stat-pill">
          <div class="pareero-health-stat-icon">🥑</div>
          <div>
            <div class="pareero-health-stat-val">${Math.round(totF)} / ${m.targetFatGrams}g</div>
            <div class="pareero-health-stat-lbl">Fat Goal</div>
          </div>
        </div>
      `;
    }

    if (diaryListContainer) {
      if (todayLog.length === 0) {
        diaryListContainer.innerHTML = `
          <div style="text-align:center; padding:28px 16px; color:var(--pareero-text-muted); background:var(--pareero-bg); border-radius:10px;">
            <p style="margin:0 0 8px;">No foods logged for today yet.</p>
            <span style="font-size:12px;">Search the Pakistani food database above or tap quick add below to log breakfast, lunch, dinner, or snacks!</span>
          </div>
        `;
      } else {
        diaryListContainer.innerHTML = todayLog.map((item, idx) => {
          const factor = item.portions || 1;
          const cal = Math.round(item.calories * factor);
          const p = Math.round(item.protein * factor);
          const c = Math.round(item.carbs * factor);
          const f = Math.round(item.fat * factor);

          return `
            <div class="pareero-health-food-item" style="border-left: 3px solid var(--pareero-primary);">
              <div class="pareero-health-food-info">
                <h4>${item.name}</h4>
                <div class="pareero-health-food-meta">
                  <span class="pareero-health-food-badge">${item.mealType || "Meal"}</span>
                  Portion: <strong>${factor}x (${item.serving})</strong>
                </div>
              </div>
              <div class="pareero-health-food-macros" style="align-items:center;">
                <div>
                  <div style="font-size:14px; font-weight:800; color:var(--pareero-primary);">${cal} kcal</div>
                  <div style="font-size:11px; color:var(--pareero-text-muted);">P: ${p}g | C: ${c}g | F: ${f}g</div>
                </div>
                <button class="pareero-health-btn pareero-health-btn-outline pareero-health-btn-sm pareero-delete-diary-item" data-index="${idx}" style="color:var(--pareero-danger); border-color:#F5C6CB;" title="Remove food item">
                  ✕
                </button>
              </div>
            </div>
          `;
        }).join("");
      }
    }
  }

  // 4. DIET PLANS & MEAL PLANNER ENGINE
  function renderDietPlansView() {
    const container = root.querySelector("#pareero-meal-plan-display");
    const m = calculateMetrics();
    const p = state.profile;

    // Default template determination
    let activePlan = state.activeMealPlan;
    if (!activePlan) {
      if (p.goal === "lose_weight") {
        activePlan = PAREERO_DATA.mealPlanTemplates.weightLoss[0];
      } else if (p.goal === "build_muscle" || p.goal === "gain_weight") {
        activePlan = PAREERO_DATA.mealPlanTemplates.muscleGain[0];
      } else {
        activePlan = PAREERO_DATA.mealPlanTemplates.maintenance[0];
      }
      state.activeMealPlan = activePlan;
    }

    if (container && activePlan) {
      let totalPlanCal = 0;
      let totalPlanProtein = 0;

      activePlan.meals.forEach((meal) => {
        totalPlanCal += meal.calories;
        totalPlanProtein += meal.protein;
      });

      container.innerHTML = `
        <div style="display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:10px; margin-bottom:18px;">
          <div>
            <h3 style="margin:0; font-size:18px; color:var(--pareero-dark);">${activePlan.title}</h3>
            <span style="font-size:12.5px; color:var(--pareero-text-muted);">Total Daily Plan: <strong>${totalPlanCal} kcal</strong> | Protein: <strong>${totalPlanProtein}g</strong></span>
          </div>
          <div style="display:flex; gap:8px;">
            <button class="pareero-health-btn pareero-health-btn-outline pareero-health-btn-sm" id="pareero-regenerate-meal-plan">
              🔄 Regenerate Plan
            </button>
            <button class="pareero-health-btn pareero-health-btn-secondary pareero-health-btn-sm" id="pareero-print-meal-plan">
              🖨️ Print Plan
            </button>
          </div>
        </div>

        <div class="pareero-health-grid-2">
          ${activePlan.meals.map((meal, idx) => {
            return `
              <div class="pareero-health-card" style="margin-bottom:0;">
                <div class="pareero-health-card-header" style="margin-bottom:10px; padding-bottom:8px;">
                  <h4 style="margin:0; font-size:15px; color:var(--pareero-primary);">${meal.name}</h4>
                  <span style="font-size:12px; font-weight:700;">${meal.calories} kcal</span>
                </div>
                <ul style="margin:0 0 10px; padding-left:18px; font-size:13px; color:var(--pareero-dark-muted);">
                  ${meal.foods.map((food) => `<li>${food}</li>`).join("")}
                </ul>
                <div style="display:flex; justify-content:space-between; align-items:center; font-size:11.5px; background:var(--pareero-bg); padding:8px 10px; border-radius:6px;">
                  <span>Macros: <strong>P: ${meal.protein}g | C: ${meal.carbs}g | F: ${meal.fat}g</strong></span>
                  <button class="pareero-health-btn pareero-health-btn-sm pareero-swap-meal-btn" data-meal-idx="${idx}" style="font-size:11px; padding:2px 8px;">
                    Swap ↺
                  </button>
                </div>
                ${meal.notes ? `<div style="font-size:11.5px; color:var(--pareero-text-muted); margin-top:8px; font-style:italic;">💡 ${meal.notes}</div>` : ""}
              </div>
            `;
          }).join("")}
        </div>
      `;

      // Render Grocery Shopping List automatically derived from the active meal plan
      renderGroceryList(activePlan);
    }
  }

  // Render Grocery Shopping List derived from active meal plan
  function renderGroceryList(plan) {
    const listContainer = root.querySelector("#pareero-grocery-list-container");
    if (!listContainer || !plan) return;

    // Collect all ingredients
    const rawItems = [];
    plan.meals.forEach((m) => {
      m.foods.forEach((f) => {
        rawItems.push(f);
      });
    });

    const uniqueItems = Array.from(new Set(rawItems));

    listContainer.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
        <span style="font-size:12px; color:var(--pareero-text-muted);">Interactive shopping checklist for this week:</span>
        <button class="pareero-health-btn pareero-health-btn-outline pareero-health-btn-sm" id="pareero-clear-grocery-checks">
          Reset Checks
        </button>
      </div>
      <div style="border:1px solid var(--pareero-card-border); border-radius:8px; overflow:hidden;">
        ${uniqueItems.map((item, idx) => {
          const isChecked = !!state.groceryListChecked[item];
          return `
            <div class="pareero-health-grocery-item ${isChecked ? "checked" : ""}" data-item-name="${item}">
              <input type="checkbox" ${isChecked ? "checked" : ""} style="cursor:pointer;" />
              <span>${item}</span>
            </div>
          `;
        }).join("")}
      </div>
    `;
  }

  // 5. FITNESS VIEW & WORKOUT GENERATOR
  function renderFitnessView() {
    const p = state.profile;
    const generatorContainer = root.querySelector("#pareero-generated-workout-container");

    // Filter exercises suitable for user's selected equipment and location
    function generatePersonalizedWorkout() {
      const duration = parseInt(root.querySelector("#pareero-workout-duration")?.value || "30", 10);
      const eq = p.equipment || "dumbbells";

      // Select warm-up, main compound movements, accessory, and cool-down
      let suitableExercises = PAREERO_DATA.exercises.filter((ex) => {
        if (eq === "none" || eq === "bodyweight") {
          return ex.equipment === "Bodyweight" || ex.equipment === "None";
        } else if (eq === "dumbbells") {
          return ex.equipment === "Dumbbells" || ex.equipment === "Bodyweight" || ex.equipment === "None";
        } else if (eq === "bands") {
          return ex.equipment === "Resistance Bands" || ex.equipment === "Bodyweight" || ex.equipment === "None";
        }
        return true; // Full Gym gets everything
      });

      if (suitableExercises.length < 4) {
        suitableExercises = PAREERO_DATA.exercises;
      }

      // Pick balanced exercises: 1 Chest/Push, 1 Back/Pull, 1 Legs, 1 Core, 1 Conditioning
      const push = suitableExercises.find((e) => e.category === "Chest" || e.category === "Shoulders") || suitableExercises[0];
      const pull = suitableExercises.find((e) => e.category === "Back") || suitableExercises[1];
      const legs = suitableExercises.find((e) => e.category === "Legs") || suitableExercises[2];
      const core = suitableExercises.find((e) => e.category === "Core") || suitableExercises[3];
      const cond = suitableExercises.find((e) => e.category === "Conditioning") || suitableExercises[4];

      const selected = [push, pull, legs, core];
      if (duration >= 45) {
        selected.push(cond);
      }

      if (generatorContainer) {
        generatorContainer.innerHTML = `
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; flex-wrap:wrap; gap:10px;">
            <div>
              <h4 style="margin:0; font-size:16px; color:var(--pareero-dark);">Custom ${duration}-Minute Session</h4>
              <span style="font-size:12px; color:var(--pareero-text-muted);">Equipment: <strong>${p.equipment}</strong> | Focus: <strong>${p.goal.replace("_", " ")}</strong></span>
            </div>
            <button class="pareero-health-btn pareero-health-btn-secondary pareero-health-btn-sm" id="pareero-print-workout-btn">
              🖨️ Print Workout
            </button>
          </div>

          <div class="pareero-health-card" style="background:var(--pareero-primary-light); border-color:var(--pareero-primary-border); margin-bottom:16px;">
            <div style="font-size:13px; font-weight:700; color:var(--pareero-primary); margin-bottom:4px;">1. DYNAMIC WARM-UP (3–5 MINS)</div>
            <p style="margin:0; font-size:12.5px; color:var(--pareero-dark-muted);">
              Arm circles, hip rotations, cat-cow, and bodyweight air squats. Elevate heart rate gradually and lubricate major joints.
            </p>
          </div>

          <div class="pareero-health-grid-2">
            ${selected.map((ex) => {
              return `
                <div class="pareero-health-card" style="margin-bottom:0;">
                  <div class="pareero-health-card-header" style="margin-bottom:8px; padding-bottom:6px;">
                    <h5 style="margin:0; font-size:14.5px; color:var(--pareero-dark);">${ex.name}</h5>
                    <span class="pareero-health-card-badge">${ex.difficulty}</span>
                  </div>
                  <div style="font-size:12px; color:var(--pareero-text-muted); margin-bottom:8px;">
                    Muscles: <strong>${ex.primaryMuscles}</strong>
                  </div>
                  <div style="display:flex; gap:12px; background:var(--pareero-bg); padding:6px 10px; border-radius:6px; font-size:12px; font-weight:700; margin-bottom:8px;">
                    <span>Sets: ${ex.sets}</span>
                    <span>Reps: ${ex.reps}</span>
                    <span>Rest: ${ex.rest}</span>
                  </div>
                  <div style="font-size:12px; color:var(--pareero-dark-muted); line-height:1.45;">
                    <strong>Coaching Cue:</strong> ${ex.cue}
                  </div>
                  <div style="font-size:11.5px; color:var(--pareero-text-muted); margin-top:6px;">
                    <em>Mod:</em> ${ex.beginnerMod}
                  </div>
                </div>
              `;
            }).join("")}
          </div>

          <div class="pareero-health-card" style="background:var(--pareero-bg); margin-top:16px; margin-bottom:0;">
            <div style="font-size:13px; font-weight:700; color:var(--pareero-dark); margin-bottom:4px;">3. RECOVERY & COOL-DOWN (3–5 MINS)</div>
            <p style="margin:0; font-size:12.5px; color:var(--pareero-text-muted);">
              Slow nasal breathing, light hamstring stretch, chest opener stretch against doorway, and downward dog.
            </p>
          </div>
        `;
      }
    }

    generatePersonalizedWorkout();

    // Render Built-In Programs List
    const programsList = root.querySelector("#pareero-preset-programs-list");
    if (programsList) {
      programsList.innerHTML = PAREERO_DATA.workoutPrograms.map((prog) => {
        return `
          <div class="pareero-health-card" style="margin-bottom:14px;">
            <div class="pareero-health-card-header" style="margin-bottom:8px; padding-bottom:8px;">
              <h4 style="margin:0; font-size:15px; color:var(--pareero-dark);">${prog.name}</h4>
              <span class="pareero-health-card-badge">${prog.daysPerWeek} Days / Wk</span>
            </div>
            <p style="font-size:12.5px; color:var(--pareero-dark-muted); margin:0 0 10px;">${prog.desc}</p>
            <div style="display:flex; justify-content:space-between; align-items:center; font-size:12px; color:var(--pareero-text-muted);">
              <span>Duration: <strong>${prog.duration}</strong></span>
              <span>Location: <strong>${prog.location}</strong></span>
            </div>
          </div>
        `;
      }).join("");
    }

    // Render Progressive Overload History
    renderOverloadLogs();
  }

  // Render Progressive Overload Logs
  function renderOverloadLogs() {
    const list = root.querySelector("#pareero-overload-logs-list");
    if (!list) return;

    if (state.overloadLogs.length === 0) {
      list.innerHTML = `
        <div style="text-align:center; padding:18px; color:var(--pareero-text-muted); font-size:13px; background:var(--pareero-bg); border-radius:8px;">
          No strength sets logged yet. Use the form above to record your exercise weight and reps to receive smart progression suggestions!
        </div>
      `;
      return;
    }

    list.innerHTML = state.overloadLogs.slice(0, 8).map((log) => {
      const ex = PAREERO_DATA.exercises.find((e) => e.id === log.exerciseId) || { name: "Exercise" };
      let suggestion = "Maintain current load";
      let suggestionColor = "var(--pareero-text-muted)";

      if (log.rpe <= 7) {
        suggestion = "Ready to increase weight (+1 to 2.5kg) or add +1 rep next session";
        suggestionColor = "var(--pareero-success)";
      } else if (log.rpe >= 9.5) {
        suggestion = "High fatigue detected; keep weight same and prioritize recovery";
        suggestionColor = "var(--pareero-warning)";
      }

      return `
        <div class="pareero-health-food-item" style="margin-bottom:8px;">
          <div>
            <h5 style="margin:0 0 2px; font-size:13.5px; color:var(--pareero-dark);">${ex.name}</h5>
            <div style="font-size:11.5px; color:var(--pareero-text-muted);">
              Date: ${log.date} | Load: <strong>${log.weight}</strong> | <strong>${log.sets} sets × ${log.reps} reps</strong> (RPE: ${log.rpe}/10)
            </div>
            <div style="font-size:11.5px; font-weight:600; color:${suggestionColor}; margin-top:4px;">
              💡 Suggestion: ${suggestion}
            </div>
          </div>
        </div>
      `;
    }).join("");
  }

  // 6. WELLNESS VIEW
  function renderWellnessView() {
    const m = calculateMetrics();
    const currentWater = state.water[state.currentDate] || 0;
    const currentSteps = state.steps[state.currentDate] || 0;
    const currentSleep = state.sleep[state.currentDate] || 7.0;
    const waterPercent = Math.min(100, Math.round((currentWater / m.targetWaterMl) * 100));

    // Water section
    const waterVal = root.querySelector("#pareero-wellness-water-val");
    const waterBar = root.querySelector("#pareero-wellness-water-bar");
    if (waterVal) waterVal.textContent = `${currentWater} / ${m.targetWaterMl} ml (${waterPercent}%)`;
    if (waterBar) waterBar.style.width = `${waterPercent}%`;

    // Steps & Sleep inputs
    const stepsInput = root.querySelector("#pareero-wellness-steps-input");
    const sleepInput = root.querySelector("#pareero-wellness-sleep-input");
    if (stepsInput) stepsInput.value = currentSteps;
    if (sleepInput) sleepInput.value = currentSleep;

    // Full 8 Habits List
    const habitsList = root.querySelector("#pareero-wellness-habits-list");
    if (habitsList) {
      const activeIds = state.habits[state.currentDate] || [];
      habitsList.innerHTML = PAREERO_DATA.habits.map((h) => {
        const isDone = activeIds.includes(h.id);
        return `
          <div class="pareero-health-habit-row ${isDone ? "completed" : ""}" data-habit-id="${h.id}">
            <div class="pareero-health-habit-info">
              <div class="pareero-health-habit-checkbox">${isDone ? "✓" : ""}</div>
              <div class="pareero-health-habit-text">
                <h5>${h.title}</h5>
                <p>${h.desc}</p>
              </div>
            </div>
          </div>
        `;
      }).join("");
    }
  }

  // 7. PROGRESS VIEW & NATIVE SVG CHARTS
  function renderProgressView() {
    const m = calculateMetrics();
    const p = state.profile;

    // Render Native SVG Weight History Chart
    const chartContainer = root.querySelector("#pareero-weight-chart-container");
    if (chartContainer) {
      renderWeightChart(chartContainer);
    }

    // Render Weekly Summary Report
    const reportBox = root.querySelector("#pareero-weekly-report-box");
    if (reportBox) {
      renderWeeklySummaryReport(reportBox, m, p);
    }
  }

  // Native SVG Line Chart Generator (Zero chart libraries)
  function renderWeightChart(container) {
    const history = state.weightHistory || [];
    if (history.length < 2) {
      container.innerHTML = `<div style="text-align:center; padding:40px; color:var(--pareero-text-muted);">Log at least 2 weight entries to view your trend chart.</div>`;
      return;
    }

    const width = 600;
    const height = 200;
    const padX = 45;
    const padY = 25;

    const weights = history.map((h) => h.weight);
    const minW = Math.floor(Math.min(...weights) - 1);
    const maxW = Math.ceil(Math.max(...weights) + 1);

    const points = history.map((item, idx) => {
      const x = padX + (idx / (history.length - 1)) * (width - padX * 2);
      const y = height - padY - ((item.weight - minW) / (maxW - minW)) * (height - padY * 2);
      return { x, y, weight: item.weight, date: item.date };
    });

    const pathD = points.reduce((acc, pt, idx) => {
      return idx === 0 ? `M ${pt.x},${pt.y}` : `${acc} L ${pt.x},${pt.y}`;
    }, "");

    const areaD = `${pathD} L ${points[points.length - 1].x},${height - padY} L ${points[0].x},${height - padY} Z`;

    container.innerHTML = `
      <svg class="pareero-health-chart-svg" viewBox="0 0 ${width} ${height}">
        <!-- Grid lines -->
        <line x1="${padX}" y1="${padY}" x2="${width - padX}" y2="${padY}" stroke="var(--pareero-card-border)" stroke-dasharray="3,3" />
        <line x1="${padX}" y1="${height / 2}" x2="${width - padX}" y2="${height / 2}" stroke="var(--pareero-card-border)" stroke-dasharray="3,3" />
        <line x1="${padX}" y1="${height - padY}" x2="${width - padX}" y2="${height - padY}" stroke="var(--pareero-card-border)" />
        
        <!-- Y-Axis labels -->
        <text x="${padX - 8}" y="${padY + 4}" font-size="10" fill="var(--pareero-text-muted)" text-anchor="end">${maxW}</text>
        <text x="${padX - 8}" y="${height / 2 + 4}" font-size="10" fill="var(--pareero-text-muted)" text-anchor="end">${Math.round((maxW + minW) / 2)}</text>
        <text x="${padX - 8}" y="${height - padY + 4}" font-size="10" fill="var(--pareero-text-muted)" text-anchor="end">${minW}</text>

        <!-- Shaded gradient area -->
        <path d="${areaD}" fill="var(--pareero-primary-light)" opacity="0.6"></path>

        <!-- Line -->
        <path d="${pathD}" fill="none" stroke="var(--pareero-primary)" stroke-width="3" stroke-linecap="round"></path>

        <!-- Points -->
        ${points.map((pt) => `
          <circle cx="${pt.x}" cy="${pt.y}" r="4" fill="var(--pareero-card-bg)" stroke="var(--pareero-primary)" stroke-width="2.5">
            <title>${pt.date}: ${pt.weight} kg</title>
          </circle>
          <text x="${pt.x}" y="${pt.y - 8}" font-size="10" font-weight="700" fill="var(--pareero-dark)" text-anchor="middle">${pt.weight}</text>
        `).join("")}
      </svg>
    `;
  }

  // Weekly Summary Report Generator
  function renderWeeklySummaryReport(box, m, p) {
    const dates = Object.keys(state.diary);
    let avgCal = m.goalCalories;
    let avgProtein = m.targetProteinGrams;
    const history = state.weightHistory || [];
    let netWeightChange = 0;

    if (history.length >= 2) {
      netWeightChange = Math.round((history[history.length - 1].weight - history[0].weight) * 10) / 10;
    }

    box.innerHTML = `
      <div style="background:var(--pareero-primary-light); border:1px solid var(--pareero-primary-border); border-radius:12px; padding:20px;">
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px; flex-wrap:wrap; gap:8px;">
          <h4 style="margin:0; font-size:16px; color:var(--pareero-dark);">Weekly Adherence & Progress Synthesis</h4>
          <span class="pareero-health-card-badge" style="background:#FFFFFF;">Encouraging & Objective</span>
        </div>
        <p style="margin:0 0 14px; font-size:13.5px; color:var(--pareero-dark-muted); line-height:1.55;">
          ${netWeightChange < 0 ? `Your recorded weight demonstrates a steady downward change of <strong>${Math.abs(netWeightChange)} kg</strong> since starting.` : netWeightChange > 0 ? `Your records show an upward change of <strong>${netWeightChange} kg</strong>, supporting your lean development.` : "Your weight has remained stable, establishing a clear baseline."}
          You have maintained an average daily target of <strong>~${m.goalCalories} kcal</strong> and a protein objective of <strong>~${m.targetProteinGrams}g</strong>.
          Remember that sustainable body composition changes occur through repeated monthly consistency rather than short-term extremes.
        </p>
        <div style="display:grid; grid-template-columns:repeat(3, 1fr); gap:10px; text-align:center;">
          <div style="background:#FFFFFF; padding:10px; border-radius:8px;">
            <div style="font-size:11px; color:var(--pareero-text-muted);">LOGGED SESSIONS</div>
            <div style="font-size:16px; font-weight:800; color:var(--pareero-primary);">${state.overloadLogs.length}</div>
          </div>
          <div style="background:#FFFFFF; padding:10px; border-radius:8px;">
            <div style="font-size:11px; color:var(--pareero-text-muted);">WEIGHT ENTRIES</div>
            <div style="font-size:16px; font-weight:800; color:var(--pareero-primary);">${history.length}</div>
          </div>
          <div style="background:#FFFFFF; padding:10px; border-radius:8px;">
            <div style="font-size:11px; color:var(--pareero-text-muted);">TARGET STATUS</div>
            <div style="font-size:16px; font-weight:800; color:var(--pareero-success);">Consistent</div>
          </div>
        </div>
      </div>
    `;
  }

  // 8. KNOWLEDGE HUB / SEO ARTICLES VIEW
  function renderKnowledgeView() {
    const listContainer = root.querySelector("#pareero-seo-articles-list");
    const readerContainer = root.querySelector("#pareero-seo-article-reader");

    if (!state.currentSeoArticle) {
      state.currentSeoArticle = PAREERO_DATA.seoArticles[0];
    }

    const current = state.currentSeoArticle;

    if (listContainer) {
      listContainer.innerHTML = PAREERO_DATA.seoArticles.map((art) => {
        const isActive = art.slug === current.slug;
        return `
          <div class="pareero-health-chip ${isActive ? "active" : ""}" data-article-slug="${art.slug}">
            ${art.title.split(":")[0]}
          </div>
        `;
      }).join("");
    }

    if (readerContainer && current) {
      readerContainer.innerHTML = `
        <article class="pareero-health-article">
          <div style="margin-bottom:12px;">
            <span class="pareero-health-card-badge">${current.category}</span>
            <span style="font-size:12px; color:var(--pareero-text-muted); margin-left:8px;">${current.readTime}</span>
          </div>
          <h1>${current.h1}</h1>
          <p style="font-size:16px; font-weight:600; color:var(--pareero-dark); line-height:1.5; margin-bottom:20px;">
            ${current.lead}
          </p>
          
          <div class="pareero-health-article-body">
            ${current.content}
          </div>

          <h3 style="margin-top:28px;">Frequently Asked Questions</h3>
          <div style="margin-top:12px;">
            ${current.faqs.map((faq) => {
              return `
                <div class="pareero-health-accordion" style="margin-bottom:8px;">
                  <div class="pareero-health-accordion-header">
                    <span>${faq.q}</span>
                    <span class="pareero-health-accordion-icon">▼</span>
                  </div>
                  <div class="pareero-health-accordion-content">
                    <p style="margin:0;">${faq.a}</p>
                  </div>
                </div>
              `;
            }).join("")}
          </div>
        </article>
      `;
    }
  }

  // Quick Action Buttons wiring
  function initEvents() {
    // Navigation tab clicks
    root.addEventListener("click", (e) => {
      const navBtn = e.target.closest("[data-nav]");
      if (navBtn) {
        e.preventDefault();
        const view = navBtn.getAttribute("data-nav");
        switchView(view);
        return;
      }

      // Quick actions
      const actionBtn = e.target.closest("[data-action]");
      if (actionBtn) {
        e.preventDefault();
        const action = actionBtn.getAttribute("data-action");
        handleQuickAction(action);
        return;
      }

      // Habit row click
      const habitRow = e.target.closest(".pareero-health-habit-row");
      if (habitRow) {
        const id = habitRow.getAttribute("data-habit-id");
        toggleHabit(id);
        return;
      }

      // Accordion toggle
      const accHeader = e.target.closest(".pareero-health-accordion-header");
      if (accHeader) {
        const acc = accHeader.closest(".pareero-health-accordion");
        acc.classList.toggle("open");
        return;
      }

      // Grocery checklist item toggle
      const groceryItem = e.target.closest(".pareero-health-grocery-item");
      if (groceryItem) {
        const name = groceryItem.getAttribute("data-item-name");
        state.groceryListChecked[name] = !state.groceryListChecked[name];
        saveState();
        renderDietPlansView();
        return;
      }

      // Add Food from database button
      const addFoodBtn = e.target.closest(".pareero-add-food-btn");
      if (addFoodBtn) {
        const foodId = addFoodBtn.getAttribute("data-food-id");
        openLogFoodModal(foodId);
        return;
      }

      // Delete Diary Item button
      const delItemBtn = e.target.closest(".pareero-delete-diary-item");
      if (delItemBtn) {
        const idx = parseInt(delItemBtn.getAttribute("data-index"), 10);
        if (state.diary[state.currentDate]) {
          state.diary[state.currentDate].splice(idx, 1);
          saveState();
          renderNutritionView();
          renderDashboard();
        }
        return;
      }

      // Swap Meal in planner
      const swapBtn = e.target.closest(".pareero-swap-meal-btn");
      if (swapBtn) {
        const idx = parseInt(swapBtn.getAttribute("data-meal-idx"), 10);
        swapMealInActivePlan(idx);
        return;
      }

      // Regenerate Plan button
      if (e.target.closest("#pareero-regenerate-meal-plan")) {
        regenerateActivePlan();
        return;
      }

      // Reset Grocery checks
      if (e.target.closest("#pareero-clear-grocery-checks")) {
        state.groceryListChecked = {};
        saveState();
        renderDietPlansView();
        return;
      }

      // SEO article selector chip
      const seoChip = e.target.closest("[data-article-slug]");
      if (seoChip) {
        const slug = seoChip.getAttribute("data-article-slug");
        const found = PAREERO_DATA.seoArticles.find((a) => a.slug === slug);
        if (found) {
          state.currentSeoArticle = found;
          renderKnowledgeView();
        }
        return;
      }

      // Print Meal Plan button
      if (e.target.closest("#pareero-print-meal-plan")) {
        window.print();
        return;
      }

      // Print Workout button
      if (e.target.closest("#pareero-print-workout-btn")) {
        window.print();
        return;
      }

      // Modal triggers & close buttons
      if (e.target.closest("#pareero-open-theme-settings-btn")) {
        openModal("theme-modal");
        return;
      }
      if (e.target.closest("#pareero-open-profile-btn") || e.target.closest("#pareero-open-profile-btn-2") || e.target.closest("#pareero-footer-profile-link")) {
        openProfileModal();
        return;
      }
      if (e.target.closest(".pareero-modal-close-btn") || e.target.classList.contains("pareero-health-modal-overlay")) {
        closeAllModals();
        return;
      }
    });

    // Food search typing
    const searchInput = root.querySelector("#pareero-food-search-input");
    if (searchInput) {
      searchInput.addEventListener("input", () => {
        renderNutritionView();
      });
    }

    const catFilter = root.querySelector("#pareero-food-cat-filter");
    if (catFilter) {
      catFilter.addEventListener("change", () => {
        renderNutritionView();
      });
    }

    const durationSelect = root.querySelector("#pareero-workout-duration");
    if (durationSelect) {
      durationSelect.addEventListener("change", () => {
        renderFitnessView();
      });
    }

    // Water intake buttons
    root.addEventListener("click", (e) => {
      if (e.target.closest("#pareero-add-water-250")) {
        addWater(250);
      } else if (e.target.closest("#pareero-add-water-500")) {
        addWater(500);
      } else if (e.target.closest("#pareero-reset-water")) {
        state.water[state.currentDate] = 0;
        saveState();
        renderWellnessView();
      }
    });

    // Steps & Sleep auto-save
    const stepsInput = root.querySelector("#pareero-wellness-steps-input");
    if (stepsInput) {
      stepsInput.addEventListener("change", (e) => {
        state.steps[state.currentDate] = parseInt(e.target.value || "0", 10);
        saveState();
      });
    }
    const sleepInput = root.querySelector("#pareero-wellness-sleep-input");
    if (sleepInput) {
      sleepInput.addEventListener("change", (e) => {
        state.sleep[state.currentDate] = parseFloat(e.target.value || "0");
        saveState();
      });
    }

    // Progressive Overload form submit
    const overloadForm = root.querySelector("#pareero-overload-form");
    if (overloadForm) {
      overloadForm.addEventListener("submit", (e) => {
        e.preventDefault();
        const exId = root.querySelector("#pareero-overload-ex-select")?.value;
        const load = root.querySelector("#pareero-overload-load")?.value;
        const sets = root.querySelector("#pareero-overload-sets")?.value;
        const reps = root.querySelector("#pareero-overload-reps")?.value;
        const rpe = parseFloat(root.querySelector("#pareero-overload-rpe")?.value || "7");

        if (!exId || !load || !sets || !reps) return;

        state.overloadLogs.unshift({
          id: "ov_" + Date.now(),
          exerciseId: exId,
          date: state.currentDate,
          weight: load,
          sets: sets,
          reps: reps,
          rpe: rpe
        });

        saveState();
        renderFitnessView();
        overloadForm.reset();
      });
    }

    // Populate Overload exercise dropdown
    const exSelect = root.querySelector("#pareero-overload-ex-select");
    if (exSelect) {
      exSelect.innerHTML = PAREERO_DATA.exercises.map((e) => `<option value="${e.id}">${e.name} (${e.category})</option>`).join("");
    }

    // Data Export & Clear Buttons
    const exportBtn = root.querySelector("#pareero-export-data-btn");
    if (exportBtn) {
      exportBtn.addEventListener("click", () => {
        exportUserData();
      });
    }

    const clearBtn = root.querySelector("#pareero-clear-data-btn");
    if (clearBtn) {
      clearBtn.addEventListener("click", () => {
        if (confirm("Are you sure you want to clear your local Pareero planner data? This cannot be undone.")) {
          localStorage.removeItem(STORAGE_KEY);
          location.reload();
        }
      });
    }

    // Theme selector listeners
    wireThemePicker();
    wireProfileForm();
  }

  // Toggle Daily Habit
  function toggleHabit(id) {
    if (!state.habits[state.currentDate]) {
      state.habits[state.currentDate] = [];
    }
    const arr = state.habits[state.currentDate];
    const idx = arr.indexOf(id);
    if (idx > -1) {
      arr.splice(idx, 1);
    } else {
      arr.push(id);
    }
    saveState();
    renderDashboard();
    renderWellnessView();
  }

  // Add Water
  function addWater(amount) {
    const cur = state.water[state.currentDate] || 0;
    state.water[state.currentDate] = cur + amount;
    saveState();
    renderWellnessView();
    renderDashboard();
  }

  // Quick Action Router
  function handleQuickAction(action) {
    switch (action) {
      case "calc-calories":
      case "check-bmi":
        switchView("weight");
        break;
      case "create-diet":
        switchView("diet-plans");
        break;
      case "create-workout":
        switchView("fitness");
        break;
      case "track-food":
        switchView("nutrition");
        break;
      case "track-weight":
        switchView("progress");
        break;
      default:
        break;
    }
  }

  // Swap Meal in Planner
  function swapMealInActivePlan(idx) {
    if (!state.activeMealPlan || !state.activeMealPlan.meals[idx]) return;
    const alternatives = [
      {
        name: "High-Protein Scramble",
        foods: ["3 Scrambled Eggs with chopped tomato", "1 Whole Wheat Roti", "1 Cup Kahwa"],
        calories: 360,
        protein: 24,
        carbs: 32,
        fat: 14
      },
      {
        name: "Lentil & Steamed Rice Bowl",
        foods: ["1.5 Cup Daal Maash (tadka)", "1 Cup Basmati Rice", "Fresh Kachumber"],
        calories: 440,
        protein: 18,
        carbs: 78,
        fat: 6
      },
      {
        name: "Grilled Chicken & Dahi Bowl",
        foods: ["180g Chicken Breast Tikka", "1 Cup Low Fat Dahi", "1 Whole Wheat Roti"],
        calories: 505,
        protein: 52,
        carbs: 38,
        fat: 12
      },
      {
        name: "Paneer & Spinach Power Lunch",
        foods: ["1 Cup Palak Paneer", "1 Roti", "1 Glass Salted Cumin Lassi"],
        calories: 550,
        protein: 26,
        carbs: 48,
        fat: 28
      }
    ];

    const randomAlt = alternatives[Math.floor(Math.random() * alternatives.length)];
    state.activeMealPlan.meals[idx] = Object.assign({}, state.activeMealPlan.meals[idx], randomAlt);
    saveState();
    renderDietPlansView();
  }

  // Regenerate Active Plan
  function regenerateActivePlan() {
    const p = state.profile;
    let templateList = PAREERO_DATA.mealPlanTemplates.weightLoss;
    if (p.goal === "build_muscle" || p.goal === "gain_weight") {
      templateList = PAREERO_DATA.mealPlanTemplates.muscleGain;
    } else if (p.goal === "maintain_weight") {
      templateList = PAREERO_DATA.mealPlanTemplates.maintenance;
    }

    const randomTemplate = templateList[Math.floor(Math.random() * templateList.length)];
    // Deep clone
    state.activeMealPlan = JSON.parse(JSON.stringify(randomTemplate));
    saveState();
    renderDietPlansView();
  }

  // Modal Control
  function openModal(id) {
    const modal = root.querySelector(`#pareero-${id}`);
    if (modal) modal.classList.add("open");
  }

  function closeAllModals() {
    root.querySelectorAll(".pareero-health-modal-overlay").forEach((m) => m.classList.remove("open"));
  }

  // Open Log Food Modal
  function openLogFoodModal(foodId) {
    const food = PAREERO_DATA.foods.find((f) => f.id === foodId);
    if (!food) return;

    const modal = root.querySelector("#pareero-log-food-modal");
    if (!modal) return;

    modal.querySelector("#pareero-log-food-name").textContent = food.name;
    modal.querySelector("#pareero-log-food-serving").textContent = food.serving;
    modal.querySelector("#pareero-log-food-cal").textContent = `${food.calories} kcal`;

    const confirmBtn = modal.querySelector("#pareero-confirm-log-btn");
    confirmBtn.onclick = function () {
      const portions = parseFloat(modal.querySelector("#pareero-log-food-portions").value || "1");
      const mealType = modal.querySelector("#pareero-log-food-meal").value || "Lunch";

      if (!state.diary[state.currentDate]) {
        state.diary[state.currentDate] = [];
      }

      state.diary[state.currentDate].push({
        id: "d_" + Date.now(),
        foodId: food.id,
        name: food.name,
        serving: food.serving,
        portions: portions,
        calories: food.calories,
        protein: food.protein,
        carbs: food.carbs,
        fat: food.fat,
        mealType: mealType
      });

      saveState();
      closeAllModals();
      renderNutritionView();
      renderDashboard();
    };

    modal.classList.add("open");
  }

  // Wire Theme Picker
  function wireThemePicker() {
    const modal = root.querySelector("#pareero-theme-modal");
    if (!modal) return;

    // Palette Options
    const paletteContainer = modal.querySelector("#pareero-palette-picker-options");
    if (paletteContainer) {
      paletteContainer.innerHTML = PAREERO_DATA.themes.palettes.map((pal) => {
        const isCurrent = state.profile.themePalette === pal.id;
        return `
          <div class="pareero-health-chip ${isCurrent ? "active" : ""}" data-set-palette="${pal.id}" style="cursor:pointer; display:flex; align-items:center; gap:6px;">
            <span style="display:inline-block; width:12px; height:12px; border-radius:50%; background:${pal.primary};"></span>
            ${pal.name.split(" ")[0]}
          </div>
        `;
      }).join("");

      paletteContainer.addEventListener("click", (e) => {
        const chip = e.target.closest("[data-set-palette]");
        if (chip) {
          const id = chip.getAttribute("data-set-palette");
          state.profile.themePalette = id;
          applyThemeSettings();
          saveState();
          wireThemePicker();
        }
      });
    }

    // Font Options
    const fontContainer = modal.querySelector("#pareero-font-picker-options");
    if (fontContainer) {
      fontContainer.innerHTML = PAREERO_DATA.themes.fonts.map((f) => {
        const isCurrent = state.profile.themeFont === f.id;
        return `
          <div class="pareero-health-chip ${isCurrent ? "active" : ""}" data-set-font="${f.id}" style="cursor:pointer;">
            ${f.name}
          </div>
        `;
      }).join("");

      fontContainer.addEventListener("click", (e) => {
        const chip = e.target.closest("[data-set-font]");
        if (chip) {
          const id = chip.getAttribute("data-set-font");
          state.profile.themeFont = id;
          applyThemeSettings();
          saveState();
          wireThemePicker();
        }
      });
    }

    // Mood Options
    const moodContainer = modal.querySelector("#pareero-mood-picker-options");
    if (moodContainer) {
      moodContainer.innerHTML = PAREERO_DATA.themes.moods.map((m) => {
        const isCurrent = state.profile.themeMood === m.id;
        return `
          <div class="pareero-health-chip ${isCurrent ? "active" : ""}" data-set-mood="${m.id}" style="cursor:pointer;">
            ${m.name}
          </div>
        `;
      }).join("");

      moodContainer.addEventListener("click", (e) => {
        const chip = e.target.closest("[data-set-mood]");
        if (chip) {
          const id = chip.getAttribute("data-set-mood");
          state.profile.themeMood = id;
          applyThemeSettings();
          saveState();
          wireThemePicker();
        }
      });
    }
  }

  // Open & Wire Profile Modal
  function openProfileModal(isInitialPrompt = false) {
    const modal = root.querySelector("#pareero-profile-modal");
    if (!modal) return;

    const p = state.profile;
    modal.querySelector("#pareero-prof-age").value = p.age;
    modal.querySelector("#pareero-prof-sex").value = p.sex;
    modal.querySelector("#pareero-prof-weight").value = p.weight;
    modal.querySelector("#pareero-prof-goal-weight").value = p.goalWeight;
    modal.querySelector("#pareero-prof-height").value = p.height;
    modal.querySelector("#pareero-prof-activity").value = p.activity;
    modal.querySelector("#pareero-prof-goal").value = p.goal;
    modal.querySelector("#pareero-prof-units").value = p.units;
    modal.querySelector("#pareero-prof-equipment").value = p.equipment;

    // Update title depending on whether it's an initial open prompt or intentional edit
    const headerTitle = modal.querySelector(".pareero-health-modal-header h3");
    if (headerTitle) {
      headerTitle.textContent = isInitialPrompt
        ? "👋 Welcome! Please Set Your Profile Stats"
        : "👤 Profile & Goals Configuration";
    }

    // Refresh dynamic unit labels
    updateProfileUnitLabels(p.units);

    modal.classList.add("open");
  }

  function updateProfileUnitLabels(units) {
    const weightLabel = root.querySelector("#pareero-label-weight");
    const goalWeightLabel = root.querySelector("#pareero-label-goal-weight");
    const heightLabel = root.querySelector("#pareero-label-height");

    if (units === "imperial") {
      if (weightLabel) weightLabel.textContent = "Current Weight (lbs):";
      if (goalWeightLabel) goalWeightLabel.textContent = "Goal Weight (lbs):";
      if (heightLabel) heightLabel.textContent = "Height (inches):";
    } else {
      if (weightLabel) weightLabel.textContent = "Current Weight (kg):";
      if (goalWeightLabel) goalWeightLabel.textContent = "Goal Weight (kg):";
      if (heightLabel) heightLabel.textContent = "Height (cm):";
    }
  }

  function wireProfileForm() {
    const form = root.querySelector("#pareero-profile-form");
    if (!form) return;

    // Listen for unit changes inside modal
    const unitsSelect = form.querySelector("#pareero-prof-units");
    if (unitsSelect) {
      unitsSelect.addEventListener("change", (e) => {
        const newUnits = e.target.value;
        const currentWeightInput = form.querySelector("#pareero-prof-weight");
        const goalWeightInput = form.querySelector("#pareero-prof-goal-weight");
        const heightInput = form.querySelector("#pareero-prof-height");

        if (newUnits === "imperial" && state.profile.units === "metric") {
          // Convert from kg to lbs and cm to inches
          if (currentWeightInput.value) currentWeightInput.value = kgToLbs(parseFloat(currentWeightInput.value));
          if (goalWeightInput.value) goalWeightInput.value = kgToLbs(parseFloat(goalWeightInput.value));
          if (heightInput.value) heightInput.value = Math.round(parseFloat(heightInput.value) / 2.54);
        } else if (newUnits === "metric" && state.profile.units === "imperial") {
          // Convert from lbs to kg and inches to cm
          if (currentWeightInput.value) currentWeightInput.value = lbsToKg(parseFloat(currentWeightInput.value));
          if (goalWeightInput.value) goalWeightInput.value = lbsToKg(parseFloat(goalWeightInput.value));
          if (heightInput.value) heightInput.value = Math.round(parseFloat(heightInput.value) * 2.54);
        }

        updateProfileUnitLabels(newUnits);
      });
    }

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const p = state.profile;
      p.age = parseInt(form.querySelector("#pareero-prof-age").value || "28", 10);
      p.sex = form.querySelector("#pareero-prof-sex").value;
      p.units = form.querySelector("#pareero-prof-units").value;
      p.weight = parseFloat(form.querySelector("#pareero-prof-weight").value || "68");
      p.goalWeight = parseFloat(form.querySelector("#pareero-prof-goal-weight").value || "62");
      p.height = parseFloat(form.querySelector("#pareero-prof-height").value || "165");
      p.activity = form.querySelector("#pareero-prof-activity").value;
      p.goal = form.querySelector("#pareero-prof-goal").value;
      p.equipment = form.querySelector("#pareero-prof-equipment").value;

      // Update today's weight in history
      const weightInKg = p.units === "imperial" ? lbsToKg(p.weight) : p.weight;
      const todayEntry = state.weightHistory.find((w) => w.date === state.currentDate);
      const metrics = calculateMetrics();
      if (todayEntry) {
        todayEntry.weight = weightInKg;
        todayEntry.bmi = metrics.bmi;
      } else {
        state.weightHistory.push({
          date: state.currentDate,
          weight: weightInKg,
          bmi: metrics.bmi
        });
      }

      saveState();
      closeAllModals();
      renderDashboard();
      renderWeightView();
      renderNutritionView();
      renderFitnessView();
      renderProgressView();
    });
  }

  // Export JSON file
  function exportUserData() {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(state, null, 2));
    const downloadAnchor = document.createElement("a");
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute("download", `pareero-health-data-${state.currentDate}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  }

  // Initialize App
  function init() {
    loadPersistedState();
    applyThemeSettings();
    initEvents();

    // Check hash for direct deep linking
    const hash = window.location.hash.replace("#", "");
    if (hash && ["dashboard", "weight", "nutrition", "diet-plans", "fitness", "wellness", "progress", "knowledge"].includes(hash)) {
      switchView(hash);
    } else {
      switchView("dashboard");
    }

    // ON PAGE OPEN: Prompt user to adjust profile stats
    setTimeout(() => {
      openProfileModal(true);
    }, 280);
  }

  // Execute on DOM Ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
